import React, { useState } from 'react';
import { KeyRound, Copy, CheckCircle, AlertTriangle } from 'lucide-react';
import { clientAPI } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

export const ClientResetPasswordDialog = ({ client, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => { setResult(null); onClose(); };

  const handleReset = async () => {
    setLoading(true);
    try {
      const res = await clientAPI.resetPassword(client.id);
      setResult(res.data);
      if (res.data.email_sent) toast.success(`New password emailed to ${res.data.email}`);
      else toast.warning('Password reset, but email could not be sent. Share it manually.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.password);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy - please copy it manually');
    }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="client-reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5" /> Reset Client Password</DialogTitle>
          <DialogDescription>{client?.organization_name}</DialogDescription>
        </DialogHeader>
        {!result ? (
          <p className="text-sm text-muted-foreground">
            A new random password will be generated and emailed to <strong>{client?.email}</strong>. The old password will stop working immediately.
          </p>
        ) : (
          <div className="space-y-3" data-testid="client-reset-password-result">
            <div className={`flex items-center gap-2 text-sm ${result.email_sent ? 'text-green-600' : 'text-amber-600'}`} data-testid="client-reset-email-status">
              {result.email_sent ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
              {result.email_sent ? `Emailed to ${result.email}` : 'Email not sent - please share manually'}
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1">
              <div><span className="text-muted-foreground">Login Email:</span> <strong>{result.email}</strong></div>
              <div className="flex items-center justify-between">
                <span><span className="text-muted-foreground">New Password:</span> <strong className="font-mono" data-testid="client-new-password">{result.password}</strong></span>
                <Button size="icon" variant="ghost" onClick={copy} data-testid="copy-client-password-btn"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close} data-testid="client-reset-close-btn">{result ? 'Done' : 'Cancel'}</Button>
          {!result && (
            <Button onClick={handleReset} disabled={loading} data-testid="client-reset-confirm-btn">
              {loading ? 'Resetting...' : 'Reset & Email Password'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
